import React, { FC, Suspense } from 'react';
import { SkeletonBlock, SkeletonStack } from 'src/components/widgets/skeleton';
import { T } from 'src/shared/helpers';
import { ContainerSmall } from '../../containers';
import SelectedOptionValue from '../../state/selectedOptionValue';
import { variantsQuery$data } from './__generated__/variantsQuery.graphql';
import OptionsList from './addVariantOptionsList';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import { ContainerBase } from '../../../../widgets/layouts';
import { MerchantProductVariantImageMatch } from './helpers';

const OptionsLoader = (
    <ContainerSmall>
        <SkeletonStack aria-hidden='true' $gap='20px'>
            <SkeletonBlock $width='150px' $height='65px' $radius='0' />
            <SkeletonBlock $width='100px' $height='21px' $radius='10px' $margin='10px 0 10px 10px' />
            {Array.from({ length: 3 }).map((_, idx) => (
                <SkeletonBlock key={idx} $height='53px' $radius='10px' />
            ))}
        </SkeletonStack>
    </ContainerSmall>
);

interface AddVariantsProps {
    onAddVariantClick: (selectedOptionsValues: SelectedOptionValue[], merchantProductVariantImageMatches: MerchantProductVariantImageMatch[] | null) => void;
    onCancelClick: () => void;
    options: variantsQuery$data['merchantProductOptions'];
    importImageVariantForSalesChannel: boolean;
}

const AddVariant: FC<AddVariantsProps> = ({ onAddVariantClick, onCancelClick, options, importImageVariantForSalesChannel }) => {
    const bannerContent = importImageVariantForSalesChannel ? (
        <>
            <strong>{T._('Variant Options', 'Admin')}</strong>
            {' — '}
            {T._(
                `This is where you can bring your product attributes from the store into the Customizer, making them available to your customers. Attributes you don't import will still appear on the product page but won't be shown in the Customizer — they'll only be passed to the cart.`,
                'Admin'
            )}{' '}
            <strong>{T._('If you import all attributes and options, Zakeke will also import the corresponding variant images.', 'Admin')}</strong>{' '}
            {T._('Otherwise, the default product image is used and can be replaced manually.', 'Admin')}{' '}
            <a href='https://zakeke.zendesk.com/hc/en-us/articles/360015501299-Variants' target='_blank' rel='noopener noreferrer'>
                {T._('Learn more', 'Admin')}
            </a>
        </>
    ) : (
        <>
            <strong>{T._('Variant Options', 'Admin')}</strong>
            {' — '}
            {T._(
                `This is where you can bring your product attributes from the store into the Customizer, making them available to your customers. Attributes you don't import will still appear on the product page but won't be shown in the Customizer — they'll only be passed to the cart. Variant images won't be imported automatically; Zakeke will use the main product image by default, and you can replace them manually if needed.`,
                'Admin'
            )}{' '}
            <a href='https://zakeke.zendesk.com/hc/en-us/articles/360015501299-Variants' target='_blank' rel='noopener noreferrer'>
                {T._('Learn more', 'Admin')}
            </a>
        </>
    );

    return (
        <ContainerBase>
            <NotificationBanner type='INFO' variant='slim' content={bannerContent} />
            <div style={{ marginTop: '20px' }} />
            <Suspense fallback={OptionsLoader}>
                <OptionsList options={options} onAddVariantClick={onAddVariantClick} onCancelClick={onCancelClick} />
            </Suspense>
        </ContainerBase>
    );
};

export default AddVariant;
